import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Stack,
  Button,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Paper,
  Alert,
  Divider,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast } from "react-toastify";
import { usePermissions } from "../login/PermissionContext";
import {
  loadDoctorConsoleConfig,
  fetchDoctorConsoleConfigForManager,
  saveDoctorConsoleConfigDraft,
  publishDoctorConsoleConfigVersion,
  deleteDoctorConsoleConfigVersion,
} from "../doctor-console/config";
import DoctorSelector from "./DoctorSelector.js";
import SectionsEditor from "./SectionsEditor.js";
import FeaturesEditor from "./FeaturesEditor.js";
import VersionHistoryPanel from "./VersionHistoryPanel.js";
import JsonPreviewPanel from "./JsonPreviewPanel.js";
import { buildOverrideFromDraft } from "./buildOverrideFromDraft.js";
import {
  fetchDoctorsForConsoleManager,
  resolveDefaultDoctorId,
} from "./loadDoctorsForManager.js";
import {
  MANAGER_COLORS,
  pageShellSx,
  pageInnerSx,
  headerPaperSx,
  toolbarPaperSx,
  contentPaperSx,
  tabsSx,
  tabPanelSx,
  primaryButtonSx,
  outlinedButtonSx,
} from "./managerTheme.js";

const TABS = ["Sections", "Features", "Version History", "Advanced (JSON)"];

const DoctorConsoleManager = () => {
  const { userInfo, loading: permissionsLoading } = usePermissions();
  const location = useLocation();

  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);

  const [draftConfig, setDraftConfig] = useState(() => loadDoctorConsoleConfig(null));
  const [versions, setVersions] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [saving, setSaving] = useState(false);

  const [activeTab, setActiveTab] = useState(0);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogMode, setSaveDialogMode] = useState("draft"); // "draft" | "publish"
  const [draftLabel, setDraftLabel] = useState("");

  const resolveSelectedDoctorId = useCallback(
    (list, sessionDoctorId) => {
      const doctorIdFromUrl = new URLSearchParams(location.search).get("doctor_id");
      if (
        doctorIdFromUrl &&
        list.some((doctor) => String(doctor.doctor_id) === String(doctorIdFromUrl))
      ) {
        return String(doctorIdFromUrl);
      }

      const defaultId = resolveDefaultDoctorId({
        doctors: list,
        userInfo,
        sessionDoctorId,
      });
      return defaultId != null ? String(defaultId) : null;
    },
    [location.search, userInfo],
  );

  // ─── load doctors list ──────────────────────────────────────────────────
  useEffect(() => {
    if (permissionsLoading) return;

    const token = sessionStorage.getItem("accessToken");
    const roleType = sessionStorage.getItem("roleType") || "";
    const sessionDoctorId = sessionStorage.getItem("doctor_id");

    if (!token) {
      const fallbackDoctorId = userInfo?.doctor_id || sessionDoctorId || null;
      if (fallbackDoctorId) {
        setDoctors([
          {
            doctor_id: fallbackDoctorId,
            name: userInfo?.name ?? userInfo?.doctor_name ?? "Current doctor",
          },
        ]);
        setSelectedDoctorId((current) =>
          current != null ? String(current) : String(fallbackDoctorId),
        );
      }
      return;
    }

    setLoadingDoctors(true);
    fetchDoctorsForConsoleManager(token)
      .then((list) => {
        setDoctors(list);
        setSelectedDoctorId((current) => {
          const doctorIdFromUrl = new URLSearchParams(location.search).get("doctor_id");
          if (
            doctorIdFromUrl &&
            list.some((doctor) => String(doctor.doctor_id) === String(doctorIdFromUrl))
          ) {
            return String(doctorIdFromUrl);
          }
          if (current != null) return String(current);
          return resolveSelectedDoctorId(list, sessionDoctorId);
        });
      })
      .catch((err) => {
        console.error("Failed to load doctors list", err);
        toast.error("Could not load doctors for this facility");
        const fallbackDoctorId = userInfo?.doctor_id || sessionDoctorId || null;
        if (fallbackDoctorId) {
          setDoctors([
            {
              doctor_id: fallbackDoctorId,
              name: userInfo?.name ?? userInfo?.doctor_name ?? "Current doctor",
            },
          ]);
          setSelectedDoctorId((current) =>
            current != null ? String(current) : String(fallbackDoctorId),
          );
        }
      })
      .finally(() => setLoadingDoctors(false));
  }, [permissionsLoading, userInfo, resolveSelectedDoctorId]);

  // ─── load config + versions for the selected doctor ────────────────────
  const loadForDoctor = useCallback((doctorId) => {
    if (!doctorId) return;
    setLoadingConfig(true);

    Promise.resolve(fetchDoctorConsoleConfigForManager(doctorId))
      .then((res) => {
        const override = res?.latestConfig ?? res?.publishedConfig ?? null;
        setDraftConfig(loadDoctorConsoleConfig(override));
        setActiveVersionId(res?.activeVersionId ?? null);
        setVersions(res?.versions ?? []);
      })
      .catch((err) => {
        console.error("Failed to load doctor console config", err);
        toast.error("Could not load config for this doctor");
        setDraftConfig(loadDoctorConsoleConfig(null));
        setVersions([]);
        setActiveVersionId(null);
      })
      .finally(() => setLoadingConfig(false));
  }, []);

  useEffect(() => {
    if (selectedDoctorId) loadForDoctor(selectedDoctorId);
  }, [selectedDoctorId, loadForDoctor]);

  // ─── draft mutation helpers ─────────────────────────────────────────────
  const updateSections = (sections) => setDraftConfig((c) => ({ ...c, sections }));
  const updateFeatures = (featuresOrUpdater) =>
    setDraftConfig((c) => ({
      ...c,
      features:
        typeof featuresOrUpdater === "function"
          ? featuresOrUpdater(c.features ?? {})
          : featuresOrUpdater,
    }));
  const updateFieldSet = (fieldSetId, patch) =>
    setDraftConfig((c) => ({
      ...c,
      fieldSets: { ...c.fieldSets, [fieldSetId]: { ...c.fieldSets[fieldSetId], ...patch } },
    }));


  // ─── save / publish / reset ─────────────────────────────────────────────
  const handleSaveDraft = async (label) => {
    setSaving(true);
    try {
      const override = buildOverrideFromDraft(draftConfig);
      await saveDoctorConsoleConfigDraft(selectedDoctorId, override, label || "Draft");
      toast.success("Draft saved");
      loadForDoctor(selectedDoctorId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndPublish = async (label) => {
    setSaving(true);
    try {
      const override = buildOverrideFromDraft(draftConfig);
      const { version } = await saveDoctorConsoleConfigDraft(
        selectedDoctorId,
        override,
        label?.trim() || "Published",
      );
      await publishDoctorConsoleConfigVersion(selectedDoctorId, version.id);
      toast.success("Published — the doctor's console will use this config now");
      loadForDoctor(selectedDoctorId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to publish");
    } finally {
      setSaving(false);
    }
  };

  const handlePublishVersion = async (versionId) => {
    try {
      await publishDoctorConsoleConfigVersion(selectedDoctorId, versionId);
      toast.success("Published");
      loadForDoctor(selectedDoctorId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to publish version");
    }
  };

  const handleDeleteVersion = async (versionId) => {
    try {
      await deleteDoctorConsoleConfigVersion(selectedDoctorId, versionId);
      toast.success("Version deleted");
      loadForDoctor(selectedDoctorId);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.status === 404 ||
          err?.status === 404 ||
          err?.message === "Version not found"
          ? "Version not found"
          : "Failed to delete version",
      );
    }
  };

  const handleRestoreVersion = (version) => {
    setDraftConfig(loadDoctorConsoleConfig(version.config));
    toast.info(`Loaded "${version.label}" into the draft — remember to Save/Publish`);
  };

  /** Loads the app-default layout into the draft only — no backend call, nothing published/deleted. */
  const handleReset = () => {
    setDraftConfig(loadDoctorConsoleConfig(null));
    toast.info("Form reset to app default — remember to Save/Publish to apply");
  };

  const overridePreview = draftConfig ? buildOverrideFromDraft(draftConfig) : null;
  const showDoctorPicker = doctors.length > 1;

  return (
    <Box sx={pageShellSx}>
      <Box sx={pageInnerSx}>
        <Paper elevation={0} sx={headerPaperSx}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ pt: 0.5 }}>
            <Box
              sx={{
                mt: 0.25,
                p: 1,
                borderRadius: 1.5,
                bgcolor: MANAGER_COLORS.accentLight,
                color: MANAGER_COLORS.primary,
                display: "flex",
              }}
            >
              <SettingsOutlinedIcon />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: MANAGER_COLORS.primaryDark, mb: 0.5 }}
              >
                Doctor Console Manager
              </Typography>
              <Typography variant="body2" sx={{ color: MANAGER_COLORS.textMuted, lineHeight: 1.6 }}>
                {showDoctorPicker
                  ? "Select a doctor and configure their console layout, sections, and features."
                  : "Configure which sections and fields appear in the doctor console."}{" "}
                FHIR-mandatory fields and sections are locked and cannot be disabled or made
                optional. Use <strong>Save &amp; Publish</strong> so changes apply in the Doctor
                Console.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={toolbarPaperSx}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            gap={2}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <DoctorSelector
              doctors={doctors}
              loading={loadingDoctors}
              selectedDoctorId={selectedDoctorId}
              onChange={(id) => setSelectedDoctorId(id == null || id === "" ? null : String(id))}
            />
            <Chip
              size="small"
              color={activeVersionId ? "success" : "default"}
              variant={activeVersionId ? "filled" : "outlined"}
              label={
                activeVersionId
                  ? "Published version active"
                  : "Using app default (no override published)"
              }
              sx={{ alignSelf: { xs: "flex-start", md: "center" }, fontWeight: 500 }}
            />
            <Box sx={{ flex: 1, display: { xs: "none", md: "block" } }} />
            <Stack direction="row" gap={1} flexWrap="wrap" justifyContent={{ xs: "stretch", md: "flex-end" }}>
              <Button
                onClick={handleReset}
                disabled={!selectedDoctorId || saving}
                sx={{ textTransform: "none", color: MANAGER_COLORS.textMuted }}
              >
                Reset to Default
              </Button>
              <Button
                variant="outlined"
                disabled={!draftConfig || saving}
                onClick={() => {
                  setSaveDialogMode("draft");
                  setSaveDialogOpen(true);
                }}
                sx={outlinedButtonSx}
              >
                Save Draft
              </Button>
              <Button
                variant="contained"
                disabled={!draftConfig || saving}
                onClick={() => {
                  setSaveDialogMode("publish");
                  setSaveDialogOpen(true);
                }}
                sx={primaryButtonSx}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : "Save & Publish"}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={contentPaperSx}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={tabsSx}>
            {TABS.map((tab) => (
              <Tab key={tab} label={tab} />
            ))}
          </Tabs>

          <Box sx={tabPanelSx}>
            {loadingConfig ? (
              <Stack alignItems="center" justifyContent="center" py={8} spacing={2}>
                <CircularProgress sx={{ color: MANAGER_COLORS.primary }} />
                <Typography variant="body2" color="text.secondary">
                  Loading configuration…
                </Typography>
              </Stack>
            ) : (
              <>
                {!selectedDoctorId && (
                  <Alert
                    severity="info"
                    icon={<InfoOutlinedIcon fontSize="inherit" />}
                    sx={{
                      mb: 2,
                      borderRadius: 1.5,
                      bgcolor: MANAGER_COLORS.accentLight,
                      color: MANAGER_COLORS.primaryDark,
                      "& .MuiAlert-icon": { color: MANAGER_COLORS.primary },
                    }}
                  >
                    Select a doctor above to load or save their console configuration. Showing the
                    app default layout below.
                  </Alert>
                )}
                {activeTab === 0 && (
                  <SectionsEditor
                    sections={draftConfig.sections}
                    fieldSets={draftConfig.fieldSets}
                    onChange={updateSections}
                    onFieldSetChange={updateFieldSet}
                  />
                )}
                {activeTab === 1 && (
                  <FeaturesEditor
                    features={draftConfig.features}
                    onChange={updateFeatures}
                    telemedicineEnabled={userInfo?.telemedicine_enabled === true}
                  />
                )}
                {activeTab === 2 && (
                  <VersionHistoryPanel
                    versions={versions}
                    activeVersionId={activeVersionId}
                    onRestore={handleRestoreVersion}
                    onPublish={handlePublishVersion}
                    onDelete={handleDeleteVersion}
                  />
                )}
                {activeTab === 3 && (
                  <JsonPreviewPanel overrideJson={overridePreview} doctorId={selectedDoctorId} />
                )}
              </>
            )}
          </Box>
        </Paper>
      </Box>

      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: { xs: "90vw", sm: 420 } },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: MANAGER_COLORS.primaryDark }}>
          {saveDialogMode === "publish" ? "Save & Publish" : "Save Draft"}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {saveDialogMode === "publish"
              ? "Name this version before publishing. It will appear in Version History and go live in the Doctor Console."
              : "Give this layout a name so you can find it later in Version History."}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Version label"
            placeholder="e.g. Cardiology quick-consult layout"
            value={draftLabel}
            onChange={(e) => setDraftLabel(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSaveDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={primaryButtonSx}
            disabled={!draftLabel.trim() || saving}
            onClick={async () => {
              const label = draftLabel.trim();
              if (saveDialogMode === "publish") {
                await handleSaveAndPublish(label);
              } else {
                await handleSaveDraft(label);
              }
              setSaveDialogOpen(false);
              setDraftLabel("");
            }}
          >
            {saveDialogMode === "publish" ? "Save & Publish" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DoctorConsoleManager;
